<ng-container *transloco="let t">
  <div class="table-head" [class.mb-3]="tableHeading()">
    @if (tableHeading()) {
      <span
        class="table-heading"
        [class.readonly]="readonly()"
        [class.expanded]="expanded()"
        >{{ tableHeading() }}</span
      >
    } @else if (mobileTableHeading()) {
      <span class="table-heading d-lg-none" [class.readonly]="readonly()">{{
        mobileTableHeading()
      }}</span>
    }

    <div class="d-flex flex-wrap ms-auto">
      <div class="animated-button" #animatedBtn>
        @if (
          !hideExpand() &&
          !expanded() &&
          !readonly() &&
          !disabled &&
          !readOnlyState
        ) {
          <button
            vinnova-button-text
            size="small"
            type="button"
            (click)="openExpandedModal(tableExpandedModal)"
            class="expand-btn d-none d-lg-block"
            [attr.aria-label]="t('question_table_edit') + ' ' + title()"
            #expandBtn
          >
            <lib-icon name="edit-2" aria-hidden="true"></lib-icon>
            <span class="btn-text">{{ t('question_table_edit') }}</span>
            <span class="animation-placeholder"></span>
            <span class="animation-placeholder"></span>
          </button>
        }
      </div>
    </div>
    @if (!expanded() && !disabled) {
      <button
        vinnova-button-secondary
        type="button"
        (click)="openExpandedModal(tableExpandedModal)"
        class="expand-btn-mobile d-lg-none"
        [attr.aria-label]="t('question_table_open') + ' ' + title()"
        #expandBtn
      >
        <span class="btn-text">{{ t('question_table_open') }}</span>
      </button>
    }
  </div>
  @if (
    maxCols < columns().length ||
    (maxCols <= columns().length && browseLarge()?.length > 1)
  ) {
    <div
      class="d-flex align-items-center browse-button mb-1"
      [class.d-none]="!expanded()"
      [class.d-lg-flex]="!expanded()"
    >
      <span>{{ t('question_table_browse') }}: </span>
      <button
        vinnova-button-primary
        size="small"
        type="button"
        [disabled]="startCol === 0"
        [attr.aria-label]="t('question_table_browse_prev')"
        class="ms-2"
        (click)="browsePrev()"
      >
        <lib-icon name="chevron-left" aria-hidden="true"></lib-icon>
      </button>
      <button
        vinnova-button-primary
        size="small"
        type="button"
        [disabled]="showRowSum()"
        [attr.aria-label]="t('question_table_browse_next')"
        class="ms-2"
        (click)="browseNext()"
      >
        <lib-icon name="chevron-right" aria-hidden="true"></lib-icon>
      </button>

      @if (showToggle()) {
        <div class="w-100 d-flex justify-content-end mb-2 d-block d-md-none">
          <lib-toggle
            [textActive]="toggleActiveText()"
            [textInactive]="toggleInactiveText()"
            [active]="false"
            (change)="toggle()"
          ></lib-toggle>
        </div>
      }
    </div>
  }

  <div
    class="table-container"
    [ngClass]="expanded() ? '' : 'd-none d-lg-block'"
    [class.invalid]="!hideValidation() && tableInvalid"
  >
    <table
      class="table"
      [ngClass]="columns().length >= 6 ? 'max-cols' : ''"
      [class]="'table-size-' + size()"
      [class.expanded]="expanded()"
      [class.readonly]="readonly() || (!expanded() && !hideExpand())"
      [class.border-left]="borderLeft()"
      #table
      [attr.aria-invalid]="
        !hideValidation() && errors && (tableInvalid || rowInvalid.length > 0)
      "
      [attr.aria-label]="title()"
      [attr.aria-describedby]="id() + '_feedback'"
    >
      @if (tierTwoColumnHeaders().length > 0) {
        <col />
      }
      @for (
        columnHeader of tierTwoColumnHeaders()
          | tableColspanPagination: startCol : maxCols;
        track $index
      ) {
        <colgroup
          [attr.span]="
            columnHeader.colspan < maxCols ? columnHeader.colspan : maxCols
          "
        ></colgroup>
      }

      <thead [ngClass]="readOnlyState ? 'readonly-state' : ''">
        @if (tierTwoColumnHeaders().length > 0) {
          <tr>
            <td></td>
            @for (
              columnHeader of tierTwoColumnHeaders()
                | tableColspanPagination: startCol : maxCols;
              track $index
            ) {
              <th
                [attr.colspan]="
                  columnHeader.colspan < maxCols
                    ? columnHeader.colspan
                    : maxCols
                "
                scope="colgroup"
                class="col-title"
              >
                <div [class.align-items-end]="tableContentToRight()">
                  {{
                    columnHeader.translationKey
                      ? t(columnHeader.translationKey)
                      : columnHeader.title
                  }}
                </div>
              </th>
            }
          </tr>
        }

        <tr [class.double-row]="hasDoubleTitleRow">
          <td>
            <div [class.align-items-end]="tableContentToRight()">
              <span></span>
              @if (!disableResize()) {
                <div
                  class="resize"
                  (mousedown)="mouseDownResizeHandler($event)"
                  (touchstart)="mouseDownResizeHandler($event)"
                ></div>
              }
            </div>
          </td>
          @for (
            column of columns() | tablePagination: startCol : maxCols;
            track $index
          ) {
            <th scope="col" class="col-title">
              <div [class.align-items-end]="tableContentToRight()">
                <span>
                  @if (
                    changes() &&
                    changes()['head'] &&
                    changes()['head'][$index + startCol]?.length > 0
                  ) {
                    <span
                      class="blue-dot"
                      [attr.aria-label]="t('question_table_column_changes')"
                      >&#x2022;</span
                    >
                  }
                  <span>{{
                    column.translationKey
                      ? t(column.translationKey)
                      : column.title
                  }}</span></span
                >
                @if (column.titleRow2) {
                  <span>{{ column.titleRow2 }}</span>
                }
                @if (!disableResize()) {
                  <div
                    class="resize"
                    (mousedown)="mouseDownResizeHandler($event)"
                    (touchstart)="mouseDownResizeHandler($event)"
                  ></div>
                }
              </div>
            </th>
          }

          @if (sumRow() && showRowSum()) {
            <th scope="col" class="col-sum">
              <div [class.align-items-end]="tableContentToRight()">
                {{ t('question_table_sum') }}
                @if (!disableResize()) {
                  <div
                    class="resize"
                    (mousedown)="mouseDownResizeHandler($event)"
                    (touchstart)="mouseDownResizeHandler($event)"
                  ></div>
                }
              </div>
            </th>
          }
        </tr>
      </thead>
      <tbody [ngClass]="readOnlyState ? 'readonly-state' : ''">
        @for (row of rows(); let rowIndex = $index; track $index) {
          <tr
            [class.invalid]="!hideValidation() && rowInvalid.includes(row?.id)"
            [class.warning]="!hideValidation() && rowWarning.includes(row?.id)"
          >
            <td>
              @if (
                changes() &&
                changes()[rowIndex] &&
                changes()[rowIndex]['head']?.length > 0
              ) {
                <span
                  class="blue-dot"
                  [attr.aria-label]="t('question_table_row_changes')"
                  >&#x2022;</span
                >
              }
              {{ row?.translationKey ? t(row?.translationKey) : row?.title }}
              @if (row?.tooltipText || row?.tooltipTextKey) {
                <div class="position-relative d-inline">
                  <div class="tooltip-target-area">
                    <div
                      class="tooltip-container"
                      role="tooltip"
                      [libTooltip]="
                        row?.tooltipTextKey
                          ? t(row?.tooltipTextKey)
                          : row?.tooltipText
                      "
                      triggers="click"
                      tabindex="0"
                    >
                      <lib-icon
                        name="help-circle"
                        class="description-icon"
                      ></lib-icon>
                    </div>
                  </div>
                </div>
              }
            </td>
            @for (columnIndex of showCol; track $index) {
              <td
                class="input-cell"
                [class.small-font]="smallNumbers"
                [class.readonly]="
                  notExpandedAndShowExpand ||
                  row?.readonly ||
                  columns()[columnIndex]?.readonly
                "
                (click)="tableClick()"
              >
                @if (
                  !(
                    notExpandedAndShowExpand ||
                    row?.readonly ||
                    columns()[columnIndex]?.readonly ||
                    readonly()
                  ) && !readOnlyState
                ) {
                  <div class="position-relative">
                    <input
                      vinnova-text-input
                      [formControl]="values[rowIndex][columnIndex].control"
                      [class.has-suffix]="suffix()"
                      [inputType]="inputType()"
                      [suffix]="suffix()"
                      [tabindex]="rowIndex === 0 && columnIndex === 0 ? 0 : -1"
                      [defaultValue]="defaultValue()"
                      [allowMinus]="allowMinus()"
                      (blur)="inputBlur($event)"
                      [attr.aria-label]="
                        (row?.translationKey
                          ? t(row?.translationKey)
                          : row?.title) +
                        ' ' +
                        columns()[columnIndex]?.title
                      "
                    />
                  </div>
                }
                @if (
                  notExpandedAndShowExpand ||
                  row?.readonly ||
                  columns()[columnIndex]?.readonly ||
                  readOnlyState ||
                  readonly()
                ) {
                  <div [class.text-end]="tableContentToRight()">
                    @if (readonlyValues) {
                      <span>
                        @if (
                          changes() &&
                          changes()[rowIndex] &&
                          changes()[rowIndex][columnIndex]?.length > 0
                        ) {
                          <span
                            class="blue-dot"
                            [class.hoverable]="
                              changes()[rowIndex][columnIndex][0].text
                            "
                            [attr.role]="
                              changes()[rowIndex][columnIndex][0].text
                                ? 'tooltip'
                                : undefined
                            "
                            [libTooltip]="
                              changes()[rowIndex][columnIndex][0].text
                                ? getChangeTextTooltip(
                                    changes()[rowIndex][columnIndex]
                                  )
                                : undefined
                            "
                            [attr.aria-description]="
                              changes()[rowIndex][columnIndex][0].text
                                ? getChangeTextDescription(
                                    changes()[rowIndex][columnIndex]
                                  )
                                : undefined
                            "
                            [attr.aria-label]="t('question_table_cell_changes')"
                            [triggers]="
                              changes()[rowIndex][columnIndex][0].text
                                ? 'hover-persist'
                                : undefined
                            "
                            [attr.tabindex]="
                              changes()[rowIndex][columnIndex][0].text
                                ? 0
                                : undefined
                            "
                            [containerClass]="
                              changes()[rowIndex][columnIndex][0].text
                                ? 'fit-content'
                                : undefined
                            "
                            >&#x2022;</span
                          >
                        }
                        @if (
                          columns() && columns()[columnIndex]?.id === 'procent'
                        ) {
                          <span>{{
                            readonlyValues?.[rowIndex]?.[columnIndex]
                              | numberFormat: activeLang
                          }}</span>
                        } @else {
                          <span>{{
                            readonlyValues?.[rowIndex]?.[columnIndex]
                              | numberFormat
                          }}</span>
                        }
                      </span>
                    }
                  </div>
                }
              </td>
            }
            @if (sumRow() && showRowSum()) {
              <td class="col-sum" [class.small-font]="smallNumbers">
                <div [class.text-end]="tableContentToRight()">
                  <span>
                    @if (
                      changes() &&
                      changes()[rowIndex] &&
                      changes()[rowIndex][columns().length]?.length > 0
                    ) {
                      <span
                        class="blue-dot"
                        [class.hoverable]="
                          changes()[rowIndex][columns().length][0].text
                        "
                        [attr.role]="
                          changes()[rowIndex][columns().length][0].text
                            ? 'tooltip'
                            : undefined
                        "
                        [libTooltip]="
                          changes()[rowIndex][columns().length][0].text
                            ? getChangeTextTooltip(
                                changes()[rowIndex][columns().length]
                              )
                            : undefined
                        "
                        [attr.aria-description]="
                          changes()[rowIndex][columns().length][0].text
                            ? getChangeTextDescription(
                                changes()[rowIndex][columns().length]
                              )
                            : undefined
                        "
                        [attr.aria-label]="t('question_table_cell_changes')"
                        [triggers]="
                          changes()[rowIndex][columns().length][0].text
                            ? 'hover-persist'
                            : undefined
                        "
                        [attr.tabindex]="
                          changes()[rowIndex][columns().length][0].text
                            ? 0
                            : undefined
                        "
                        [containerClass]="
                          changes()[rowIndex][columns().length][0].text
                            ? 'fit-content'
                            : undefined
                        "
                        >&#x2022;</span
                      >
                    }
                    <span>{{ rowSums[rowIndex] | numberFormat }}</span></span
                  >
                </div>
              </td>
            }
          </tr>
        }
      </tbody>
      @if (sumCol()) {
        <tfoot [ngClass]="readOnlyState ? 'readonly-state' : ''">
          <tr>
            <td>{{ totalHeading() ?? t('question_table_total') }}</td>
            @for (columnIndex of showCol; track $index) {
              @if (colSums[columnIndex] !== undefined) {
                <td class="col-sum" [class.small-font]="smallNumbers">
                  <div [class.text-end]="tableContentToRight()">
                    <span>
                      @if (
                        changes() &&
                        changes()[rows().length] &&
                        changes()[rows().length][columnIndex]?.length > 0
                      ) {
                        <span
                          class="blue-dot"
                          [class.hoverable]="
                            changes()[rows().length][columnIndex][0].text
                          "
                          [attr.role]="
                            changes()[rows().length][columnIndex][0].text
                              ? 'tooltip'
                              : undefined
                          "
                          [libTooltip]="
                            changes()[rows().length][columnIndex][0].text
                              ? getChangeTextTooltip(
                                  changes()[rows().length][columnIndex]
                                )
                              : undefined
                          "
                          [attr.aria-description]="
                            changes()[rows().length][columnIndex][0].text
                              ? getChangeTextDescription(
                                  changes()[rows().length][columnIndex]
                                )
                              : undefined
                          "
                          [attr.aria-label]="t('question_table_cell_changes')"
                          [triggers]="
                            changes()[rows().length][columnIndex][0].text
                              ? 'hover-persist'
                              : undefined
                          "
                          [attr.tabindex]="
                            changes()[rows().length][columnIndex][0].text
                              ? 0
                              : undefined
                          "
                          [containerClass]="
                            changes()[rows().length][columnIndex][0].text
                              ? 'fit-content'
                              : undefined
                          "
                          >&#x2022;</span
                        >
                      }
                      @if (
                        columns() && columns()[columnIndex]?.id === 'procent'
                      ) {
                        <span>{{
                          colSums[columnIndex] | numberFormat: activeLang
                        }}</span>
                      } @else {
                        <span>{{ colSums[columnIndex] | numberFormat }}</span>
                      }
                    </span>
                  </div>
                </td>
              }
            }
            @if (sumRow() && showRowSum()) {
              <td class="col-sum" [class.small-font]="smallNumbers">
                <div [class.text-end]="tableContentToRight()">
                  <span>
                    @if (
                      changes() &&
                      changes()[rows().length] &&
                      changes()[rows().length][columns().length]?.length > 0
                    ) {
                      <span
                        class="blue-dot"
                        [class.hoverable]="
                          changes()[rows().length][columns().length][0].text
                        "
                        [attr.role]="
                          changes()[rows().length][columns().length][0].text
                            ? 'tooltip'
                            : undefined
                        "
                        [libTooltip]="
                          changes()[rows().length][columns().length][0].text
                            ? getChangeTextTooltip(
                                changes()[rows().length][columns().length]
                              )
                            : undefined
                        "
                        [attr.aria-description]="
                          changes()[rows().length][columns().length][0].text
                            ? getChangeTextDescription(
                                changes()[rows().length][columns().length]
                              )
                            : undefined
                        "
                        [attr.aria-label]="t('question_table_cell_changes')"
                        [triggers]="
                          changes()[rows().length][columns().length][0].text
                            ? 'hover-persist'
                            : undefined
                        "
                        [attr.tabindex]="
                          changes()[rows().length][columns().length][0].text
                            ? 0
                            : undefined
                        "
                        [containerClass]="
                          changes()[rows().length][columns().length][0].text
                            ? 'fit-content'
                            : undefined
                        "
                        >&#x2022;</span
                      >
                    }
                    <span>{{ totalSum | numberFormat }}</span>
                  </span>
                </div>
              </td>
            }
          </tr>
        </tfoot>
      }
    </table>
  </div>
  @if (!hideValidation()) {
    <div class="feedback" [attr.id]="id() + '_feedback'">
      @if (!readonly() && errors && (tableInvalid || rowInvalid.length > 0)) {
        @if (
          rowInvalid.length > 0 &&
          errors.includes(BudgetValidation.MottagareNotFinansiar)
        ) {
          <lib-feedback-message class="invalid-feedback" type="invalid">
            {{ t('question_budget_validation_mottagare_not_finansiar') }}
          </lib-feedback-message>
        }
        @if (
          rowInvalid.length > 0 &&
          errors.includes(BudgetValidation.FinansiarRequired) &&
          !errors.includes(BudgetValidation.MottagareNotFinansiar)
        ) {
          <lib-feedback-message class="invalid-feedback" type="invalid">
            {{ t('question_budget_validation_finansiering_required') }}
          </lib-feedback-message>
        }
        @if (
          rowInvalid.length > 0 &&
          errors.includes(BudgetValidation.EgenFinansieringRequired)
        ) {
          <lib-feedback-message class="invalid-feedback" type="invalid">
            {{ t('question_budget_validation_egen_finansiering_required') }}
          </lib-feedback-message>
        }
        @if (
          !readonly() &&
          errors.includes('required') &&
          !errors.includes('SkissBudgetKostnaderRequired')
        ) {
          <lib-feedback-message class="invalid-feedback" type="invalid">
            {{ t('question_table_required') }}
          </lib-feedback-message>
        }
        @if (
          tableInvalid && errors.includes(BudgetValidation.KostnaderRequired)
        ) {
          <lib-feedback-message class="invalid-feedback" type="invalid">
            {{ t('question_budget_validation_kostnader_required') }}
          </lib-feedback-message>
        }
        @if (
          (tableInvalid || errors.includes('required')) &&
          errors.includes('SkissBudgetKostnaderRequired')
        ) {
          <lib-feedback-message class="invalid-feedback" type="invalid">
            {{ t('question_table_required') }}
          </lib-feedback-message>
        }
      }
      @if (
        warnings &&
        rowWarning.length > 0 &&
        warnings.includes(BudgetValidation.Under10KWarning)
      ) {
        <lib-feedback-message type="warning">
          {{ t('question_budget_warning_belopp_under') }}
        </lib-feedback-message>
      }
      @if (
        warnings &&
        warnings.includes(BudgetValidation.IndirektaKostnaderOverLonekostnader)
      ) {
        <lib-feedback-message type="warning">
          <span [innerHTML]="t('question_budget_indirect_cost_warning')"></span>
        </lib-feedback-message>
      }
      @if (warnings && !readonly()) {
        @if (warnings.includes('BudgetNoBudgetForKostnadslag')) {
          <lib-feedback-message type="warning" class="mt-3 mt-lg-0">
            {{ t('question_budget_warning_no_budget') }}
          </lib-feedback-message>
        }
        @if (
          warnings.includes('BudgetNoCostsReportedWarning') && touchedSignal()
        ) {
          <lib-feedback-message type="warning" class="mt-3 mt-lg-0">
            {{ t('question_budget_warning_no_reported_costs') }}
          </lib-feedback-message>
        }
      }
    </div>
  }
  @if (expanded() && !readOnlyState) {
    <div class="d-none d-md-flex justify-content-end">
      <button
        vinnova-button-secondary
        size="medium"
        type="button"
        (click)="onCloseExpandedModal(false)"
        class="me-3"
      >
        {{ t('question_table_expand_cancel') }}
      </button>
      <button
        vinnova-button-primary
        size="medium"
        type="button"
        (click)="onCloseExpandedModal(true)"
      >
        {{ t('question_table_expand_done') }}
      </button>
    </div>
    <div class="d-flex d-md-none flex-column mt-4">
      <button
        vinnova-button-primary
        type="button"
        (click)="onCloseExpandedModal(true)"
        class="w-100 mb-3"
      >
        {{ t('question_table_expand_done') }}
      </button>
      <button
        vinnova-button-secondary
        type="button"
        (click)="onCloseExpandedModal(false)"
        class="w-100"
      >
        {{ t('question_table_expand_cancel') }}
      </button>
    </div>
  } @else if (expanded()) {
    <button
      vinnova-button-primary
      type="button"
      (click)="onCloseExpandedModal(false)"
      class="w-100"
    >
      {{ t('question_table_expand_close') }}
    </button>
  }

  <ng-template #tableExpandedModal>
    <lib-modal
      [title]="expansionTitle()"
      [subTitle]="expansionSubTitle()"
      [showCloseButton]="true"
      (closeModal)="onCloseExpandedModal(false)"
      id="expanded-table-modal"
    >
      @if (readonly()) {
        <lib-interactive-table
          [id]="id() + '_expanded'"
          [title]="title()"
          [expanded]="true"
          [maxColsDesktop]="7"
          [maxColsMobile]="maxColsMobile() ? maxColsMobile() : 4"
          [sumRow]="sumRow()"
          [sumCol]="sumCol()"
          [readonly]="true"
          [rows]="rows()"
          [columns]="columns()"
          [tierTwoColumnHeaders]="tierTwoColumnHeaders()"
          [expandedModalInput]="expandedModalRef"
          [inputType]="inputType()"
          [suffix]="suffix()"
          [readOnly$]="readOnly$()"
          [readonlyData]="readonlyData()"
          [tableContentToRight]="tableContentToRight()"
          [tableHeading]="tableHeading()"
          [totalHeading]="totalHeading()"
          [size]="size()"
          [disableResize]="true"
          [touched]="touchedSignal()"
          [browseLarge]="browseLarge()"
          [browseMedium]="browseMedium()"
          [browseSmall]="browseSmall()"
          [changes]="changes()"
          [showToggle]="showToggle()"
          [toggleActiveText]="toggleActiveText()"
          [toggleInactiveText]="toggleInactiveText()"
          (toggleOutput)="toggle()"
        ></lib-interactive-table>
      } @else {
        <lib-interactive-table
          [id]="id() + '_expanded'"
          [title]="title()"
          [expanded]="true"
          [maxColsDesktop]="7"
          [maxColsMobile]="maxColsMobile() ? maxColsMobile() : 4"
          [sumRow]="sumRow()"
          [sumCol]="sumCol()"
          [formControl]="ngControl?.control"
          [rows]="rows()"
          [columns]="columns()"
          [tierTwoColumnHeaders]="tierTwoColumnHeaders()"
          [required]="required()"
          [expandedModalInput]="expandedModalRef"
          [inputType]="inputType()"
          [suffix]="suffix()"
          [readonlyData]="readonlyData()"
          [tableContentToRight]="tableContentToRight()"
          [tableHeading]="tableHeading()"
          [totalHeading]="totalHeading()"
          [size]="size()"
          [readOnly$]="readOnly$()"
          [tableValidation]="tableValidation()"
          [tableRowValidation]="tableRowValidation()"
          [disableResize]="true"
          [defaultValue]="defaultValue()"
          [allowMinus]="allowMinus()"
          [touched]="touchedSignal()"
          [browseLarge]="browseLarge()"
          [browseMedium]="browseMedium()"
          [browseSmall]="browseSmall()"
          (saveExpanded)="saveExpansionValues($event)"
          [changes]="changes()"
          [showToggle]="showToggle()"
          [toggleActiveText]="toggleActiveText()"
          [toggleInactiveText]="toggleInactiveText()"
          (toggleOutput)="toggle()"
        ></lib-interactive-table>
      }
    </lib-modal>
  </ng-template>
</ng-container>
